/**
 * Canvas 背景资源管理。
 *
 * 背景与姿态检测输入分离：检测始终使用摄像头，
 * 此处仅负责为 Canvas 背景层提供 camera / 图片 / 视频源。
 * 图片与视频来自本地 File（File System Access API）。
 */
import {STATE} from './params';

export class BackgroundManager {
  constructor() {
    this.image = null;
    this.bgVideo = null;
    this.loadedType = null;
    this.imageObjectUrl = null;
    this.videoObjectUrl = null;
    this.imageFileName = '';
    this.videoFileName = '';
  }

  revokeImageUrl() {
    if (this.imageObjectUrl != null) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = null;
    }
  }

  revokeVideoUrl() {
    if (this.videoObjectUrl != null) {
      URL.revokeObjectURL(this.videoObjectUrl);
      this.videoObjectUrl = null;
    }
  }

  /**
   * 根据 GUI 选择的 type 切换背景资源。
   * img/video 依赖已通过 setLocalImage/setLocalVideo 设置的本地文件。
   */
  async update(cameraVideo) {
    const type = STATE.background.type;

    if (type === 'camera') {
      if (this.bgVideo != null) {
        this.bgVideo.pause();
      }
      this.loadedType = 'camera';
      return cameraVideo;
    }

    if (type === 'img') {
      if (this.bgVideo != null) {
        this.bgVideo.pause();
      }
      if (this.image == null) {
        this.loadedType = null;
        return null;
      }
      this.loadedType = 'img';
      return this.image;
    }

    if (type === 'video') {
      if (this.bgVideo == null) {
        this.loadedType = null;
        return null;
      }
      this.loadedType = 'video';
      if (this.bgVideo.paused) {
        await this.bgVideo.play();
      }
      return this.bgVideo;
    }

    return cameraVideo;
  }

  /** 设置本地图片文件并加载 */
  async setLocalImage(file) {
    this.revokeImageUrl();
    this.image = null;
    this.imageFileName = '';
    const url = URL.createObjectURL(file);
    this.imageObjectUrl = url;
    this.image = await this.loadImage(url);
    this.imageFileName = file.name;
    if (STATE.background.type === 'img') {
      this.loadedType = 'img';
    }
  }

  /** 设置本地视频文件并加载（静音循环） */
  async setLocalVideo(file) {
    if (this.bgVideo != null) {
      this.bgVideo.pause();
      this.bgVideo.removeAttribute('src');
      this.bgVideo.load();
    }
    this.revokeVideoUrl();
    this.bgVideo = null;
    this.videoFileName = '';
    const url = URL.createObjectURL(file);
    this.videoObjectUrl = url;
    this.bgVideo = await this.loadVideo(url);
    this.videoFileName = file.name;
    if (STATE.background.type === 'video') {
      this.loadedType = 'video';
    }
  }

  /** 切换背景类型时，停止视频播放（保留已选文件） */
  pauseVideo() {
    if (this.bgVideo != null) {
      this.bgVideo.pause();
    }
  }

  /** 当前背景类型是否已具备可绘制资源 */
  isReady() {
    const type = STATE.background.type;
    if (type === 'camera') {
      return true;
    }
    if (type === 'img') {
      return this.image != null;
    }
    if (type === 'video') {
      return this.bgVideo != null;
    }
    return false;
  }

  getStatusLabel() {
    const type = STATE.background.type;
    if (type === 'camera') {
      return '摄像头';
    }
    if (type === 'img') {
      return this.imageFileName ? `图片: ${this.imageFileName}` : '图片: 未选择';
    }
    if (type === 'video') {
      return this.videoFileName ? `视频: ${this.videoFileName}` : '视频: 未选择';
    }
    return type;
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }

  loadVideo(src) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.onloadeddata = async () => {
        try {
          await video.play();
          resolve(video);
        } catch (error) {
          // 未开始运行时允许先加载，不强制 play
          resolve(video);
        }
      };
      video.onerror = () => reject(new Error(`Failed to load video: ${src}`));
      video.src = src;
    });
  }

  getDrawSource(cameraVideo) {
    const type = STATE.background.type;
    if (type === 'img') {
      return this.image;
    }
    if (type === 'video') {
      return this.bgVideo;
    }
    return cameraVideo;
  }
}
